import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { BookmarkPlus, Plus, SlidersHorizontal, Trash2, X } from "lucide-react";
import { Menu, MenuItem, MenuTrigger, Popover } from "react-aria-components";
import { useAgentFocus } from "@/app/routes/dashboard/agent/agentFocusStore";
import DashboardPage from "@/components/DashboardPage";
import { cn } from "@/utils/helpers";
import { usePermissions } from "@/hooks/usePermissions";
import { useGetBrands, type Brand } from "@/hooks/useGetBrands";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useAuthStore } from "@/stores/authStore";
import { queue } from "@/components/Toast";
import { KitButton } from "@/app/routes/dashboard/components/kit";
import { PlaybackProvider } from "@/app/routes/dashboard/components/mediaPlayback";
import {
  toggleAxis,
  WallBar,
  WallCaption,
  WallFacet,
  WallFilterAnnouncer,
  WallHairline,
  WallIncompleteNotice,
  WallPlayback,
  WallRail,
  WallReadout,
  WallSearch,
  WallViewHeader,
  type WallFacetOption,
  type WallRailOption,
} from "@/app/routes/dashboard/components/WallBar";
import WallScrolledRow from "@/app/routes/dashboard/components/WallScrolledRow";
import WallFilterPanel, { type WallFilterGroup } from "@/app/routes/dashboard/components/WallFilterPanel";
import { EmptyState, ErrorState, FilteredEmptyState } from "@/app/routes/dashboard/components/ViewStates";
import type {
  Reference,
  ReferenceCollection,
  ReferencePlatform,
  ReferenceSubmission,
  ReferenceType,
} from "@/types/reference.type";
import {
  referenceFanoutPart,
  referenceSingleView,
  referenceWallPlan,
  useDeleteReference,
  useReferenceDetail,
  useReferencesFanout,
  useReferencesInfinite,
  useReferenceSubmissions,
  useUpdateReference,
  type ReferenceFilters,
  type ReferenceListState,
} from "./api/useReferences";
import { useDeleteCollection, useReferenceCollections } from "./api/useReferenceCollections";
import {
  mergeFanout,
  mergedWallView,
  type FanoutMerge,
  type WallView,
} from "@/app/routes/dashboard/components/wallFanout";
import ReferenceTile from "./components/ReferenceTile";
import ReferenceDetail from "./components/ReferenceDetail";
import ReferenceSaveSheet from "./components/ReferenceSaveSheet";
import SubmissionActivity, { submissionActivity } from "./components/SubmissionActivity";
import CollectionsRow from "./components/CollectionsRow";
import CollectionPicker from "./components/CollectionPicker";
import ReferenceSelectBar from "./components/ReferenceSelectBar";
import ConfirmDelete from "./components/ConfirmDelete";
import { PLATFORM_LABEL, PLATFORM_OPTIONS, recreatePrompt, TYPE_LABEL } from "./components/referenceMeta";
import "./referenceWall.css";
import "./referenceParts.css";

/**
 * References: the library wall (CreativeOS · 06 References.pen, the elevated
 * References, style B).
 *
 * The wall says "this is what good looks like for this brand". The why a person
 * wrote leads every tile; the machine title and the source follow. Collections
 * sit under the title, and a collection opens as its own wall. Scope defaults to
 * the brand plus Everyone. The detail is a sheet; Save takes links, pictures and
 * ideas together, each with its own why.
 */

const SUBHEAD = "Creative references, saved by the team. Explore the work and why it matters.";
const PAGE_SIZE = 24;
const SKELETON_HEIGHTS = [520, 340, 380, 120, 420, 260, 300, 460, 220, 360, 400, 280];
const MIN_QUERY = 2;
const PHONE_QUERY = "(max-width: 767px)";

/**
 * Scope: whose references these are. The brand plus Everyone is the default
 * (the list route's includeGlobal), then the brand alone, then Everyone's alone.
 * A client (guest) sees their brand only.
 */
export type ReferenceScope = "brand-everyone" | "brand" | "global";

/* Post leaves the Kind filter (Pen); a saved trend keeps its own. */
const KIND_TYPES: ReferenceType[] = ["video", "image", "article", "note", "trend"];
const KIND_OPTIONS: WallRailOption<ReferenceType>[] = KIND_TYPES.map((t) => ({
  value: t,
  label: TYPE_LABEL[t],
  text: TYPE_LABEL[t],
}));
const SOURCE_OPTIONS: WallFacetOption<ReferencePlatform>[] = PLATFORM_OPTIONS.map((p) => ({
  value: p,
  label: PLATFORM_LABEL[p],
}));

export interface ReferencesPreview {
  list: (filters: ReferenceFilters) => ReferenceListState;
  onPlan?: (requests: readonly ReferenceFilters[]) => void;
  submissions: ReferenceSubmission[];
  brands: Brand[];
  activeBrandId: string | null;
  canEdit: boolean;
}

const MORE_SKELETON_COUNT = 8;

function SkeletonTile({ height }: { height: number }) {
  return (
    <div className="ref-cell ref-skeleton-tile" aria-hidden="true">
      <div className="ref-skeleton-media co-skeleton" style={{ height }} />
      <div className="ref-skeleton-caption">
        {[120, 200, 180, 210, 100].map((w, i) => (
          <span
            key={i}
            className="ref-skeleton-line co-skeleton"
            style={{ width: w, height: [11, 20, 20, 13, 12][i] }}
          />
        ))}
      </div>
    </div>
  );
}

function SkeletonWall() {
  return (
    <div className="co-wall-field">
      <div className="co-wall ref-wall" aria-hidden="true">
        {SKELETON_HEIGHTS.map((h, i) => (
          <SkeletonTile key={i} height={h} />
        ))}
      </div>
    </div>
  );
}

export default function ReferencesPage({ preview }: { preview?: ReferencesPreview }) {
  useAgentFocus({ surface: "references" });
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isPhone = useMediaQuery(PHONE_QUERY);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const [q, setQ] = useState("");
  const [submittedQ, setSubmittedQ] = useState("");
  const [types, setTypes] = useState<ReferenceType[]>([]);
  const [platform, setPlatform] = useState<ReferencePlatform | undefined>(undefined);
  const [scope, setScope] = useState<ReferenceScope | null>(null);
  const [page, setPage] = useState(1);
  const [editWhy, setEditWhy] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [picker, setPicker] = useState<{ references: Reference[]; anchor: HTMLElement; startNew?: boolean } | null>(null);
  const [deletingCollection, setDeletingCollection] = useState<ReferenceCollection | null>(null);

  /* The open collection and the open reference live in the URL, so Copy link
     and the browser's Back both work. */
  const collectionId = params.get("collection");
  const selectedId = params.get("ref");
  const setParam = useCallback(
    (key: string, value: string | null) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: key === "ref" && !value },
      ),
    [setParams],
  );

  const livePermissions = usePermissions();
  const { data: liveBrands } = useGetBrands();
  const liveActiveBrandId = useWorkspaceStore((s) => s.selectedBrandId);
  const isGuest = preview ? false : livePermissions.isGuest;
  const canEdit = preview ? preview.canEdit : livePermissions.canEdit || isGuest;
  const myUserId = useAuthStore((s) => s.user?.id ?? null);
  /* A client manages only what they added; the server enforces it too. */
  const canManage = useCallback(
    (reference: Pick<Reference, "createdByUserId"> | null) =>
      canEdit && (!isGuest || (!!reference && reference.createdByUserId === myUserId)),
    [canEdit, isGuest, myUserId],
  );
  const brands = useMemo(() => (preview ? preview.brands : (liveBrands ?? [])), [preview, liveBrands]);
  const activeBrandId = preview ? preview.activeBrandId : liveActiveBrandId;
  const activeBrandName = brands.find((b) => b.id === activeBrandId)?.name ?? null;
  const allowEveryone = !isGuest;

  /* Brand plus Everyone by default; Everyone alone until a brand is chosen. */
  const scopeAvailable = Boolean(activeBrandId && activeBrandName);
  const defaultScope: ReferenceScope = !scopeAvailable ? "global" : isGuest ? "brand" : "brand-everyone";
  const effectiveScope: ReferenceScope =
    !scopeAvailable ? "global" : isGuest ? "brand" : (scope ?? defaultScope);

  /* The collections: one read, picked down to the scope's. */
  const collectionsQuery = useReferenceCollections({ enabled: !preview });
  const allCollections = collectionsQuery.collections;
  const scopedCollections = useMemo(
    () =>
      allCollections.filter((c) =>
        effectiveScope === "global"
          ? c.brandId === null
          : effectiveScope === "brand"
            ? c.brandId === activeBrandId
            : c.brandId === activeBrandId || c.brandId === null,
      ),
    [allCollections, effectiveScope, activeBrandId],
  );
  const openCollection = collectionId ? (allCollections.find((c) => c.id === collectionId) ?? null) : null;
  const canManageCollection = useCallback(
    (c: ReferenceCollection) => canEdit && (!isGuest || c.createdBy.id === myUserId),
    [canEdit, isGuest, myUserId],
  );

  /* A collection is its own wall: its references, whoever they are for. */
  const base = useMemo<Omit<ReferenceFilters, "type" | "page" | "limit">>(() => {
    const common = { platform, q: submittedQ || undefined };
    if (collectionId) return { ...common, collectionId };
    if (effectiveScope === "global") return { ...common, brandId: "global" };
    return {
      ...common,
      brandId: activeBrandId ?? "global",
      includeGlobal: effectiveScope === "brand-everyone" || undefined,
    };
  }, [platform, submittedQ, collectionId, effectiveScope, activeBrandId]);

  const plan = useMemo(() => referenceWallPlan(base, types, 1, PAGE_SIZE), [base, types]);
  const fanned = plan.requests.length > 1;
  const single = useReferencesInfinite(plan.requests[0], { enabled: !preview && !fanned });
  const merged = useReferencesFanout(plan.requests, { enabled: !preview && fanned, capped: plan.capped });

  const shownCap = page * PAGE_SIZE;
  const { view, hasMore } = useMemo((): { view: WallView<Reference>; hasMore: boolean } => {
    const revealed = (all: FanoutMerge<Reference>) => ({
      view: mergedWallView(all, 1, shownCap, plan.requests.length),
      hasMore: all.rows.length > shownCap,
    });
    if (preview) {
      preview.onPlan?.(plan.requests);
      if (!fanned) {
        const pages = Array.from({ length: page }, (_, i) => preview.list({ ...plan.requests[0], page: i + 1 }));
        const list = { ...pages[0], references: pages.flatMap((p) => p.references) };
        return { view: referenceSingleView(list, PAGE_SIZE), hasMore: list.references.length < list.total };
      }
      const answers = plan.requests.map((request) => preview.list(request));
      return revealed(
        mergeFanout(
          answers.map((answer, i) => referenceFanoutPart(answer, plan.requests[i])),
          (r) => r.id,
          (r) => Date.parse(r.createdAt),
          plan.capped,
        ),
      );
    }
    if (!fanned) return { view: referenceSingleView(single, PAGE_SIZE), hasMore: single.hasMore };
    return revealed(merged);
  }, [preview, plan, fanned, page, shownCap, single, merged]);

  const { rows: visible, isLoading, isFetching, isError, refetch } = view;
  const isFetchingMore = !preview && !fanned && single.isFetchingMore;

  /* A collection's wall is cached for five minutes, and filing references into
     it does not replace that read. Opening one always asks again. */
  const refetchWallRef = useRef(refetch);
  refetchWallRef.current = refetch;
  useEffect(() => {
    if (preview || !collectionId) return;
    refetchWallRef.current();
  }, [collectionId, preview]);

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    if (preview || fanned) setPage((p) => p + 1);
    else single.loadMore();
  }, [hasMore, preview, fanned, single]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef(loadMore);
  useEffect(() => {
    loadMoreRef.current = loadMore;
  });
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || isFetchingMore || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreRef.current();
      },
      { rootMargin: "0px 0px 800px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isFetchingMore, visible.length]);

  /* The condensed bar takes over once the view header has scrolled away. */
  const [pastHeader, setPastHeader] = useState(false);
  const fieldRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    fieldRef.current =
      rootRef.current?.closest<HTMLElement>(".co-grid-field") ?? (document.scrollingElement as HTMLElement | null);
  }, [isPhone]);
  useEffect(() => {
    if (isPhone) return;
    const header = rootRef.current?.querySelector(".wb-head");
    const field = fieldRef.current;
    if (!header || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => setPastHeader(entries.every((entry) => !entry.isIntersecting)),
      { root: field && field !== document.scrollingElement ? field : null, threshold: 0 },
    );
    observer.observe(header);
    return () => observer.disconnect();
  }, [isPhone]);
  const [rowSearchFocused, setRowSearchFocused] = useState(false);

  const { submissions: liveSubmissions } = useReferenceSubmissions({ enabled: !preview });
  const activity = useMemo(
    () => submissionActivity(preview ? preview.submissions : liveSubmissions),
    [preview, liveSubmissions],
  );

  /* The open reference: from the wall, or read on its own for a deep link. */
  const fromWall = visible.find((r) => r.id === selectedId) ?? null;
  const deepLinked = useReferenceDetail(selectedId && !fromWall ? selectedId : null, { enabled: !preview });
  const selected = fromWall ?? deepLinked.data ?? null;
  const selectedIndex = fromWall ? visible.indexOf(fromWall) : -1;

  const filtered = Boolean(submittedQ || types.length > 0 || platform || effectiveScope !== defaultScope);
  const shownTotal = view.count;
  const [libraryTotal, setLibraryTotal] = useState<number | null>(null);
  if (!filtered && !collectionId && !isLoading && !isError && libraryTotal !== shownTotal) {
    setLibraryTotal(shownTotal);
  }

  const scopeLabel = (s: ReferenceScope) =>
    s === "global" ? "Everyone" : s === "brand" ? (activeBrandName ?? "This brand") : `${activeBrandName ?? "This brand"} + Everyone`;

  const activeSummary = [
    submittedQ ? `"${submittedQ}"` : null,
    types.length ? types.map((t) => TYPE_LABEL[t]).join(" or ") : null,
    platform ? PLATFORM_LABEL[platform] : null,
    effectiveScope === defaultScope ? null : scopeLabel(effectiveScope),
  ].filter((bit): bit is string => bit !== null);

  const reset = () => {
    setQ("");
    setSubmittedQ("");
    setTypes([]);
    setPlatform(undefined);
    setScope(null);
    setPage(1);
  };
  const runSearch = (term: string) => {
    setQ(term);
    setSubmittedQ(term);
    setPage(1);
  };
  const submitSearch = () => {
    const next = q.trim();
    if (next.length > 0 && next.length < MIN_QUERY) return;
    setPage(1);
    setSubmittedQ(next);
  };
  const searchNeedsMore = q.trim().length > 0 && q.trim().length < MIN_QUERY;
  const pickType = (next: ReferenceType) => {
    setTypes((set) => toggleAxis(set, next));
    setPage(1);
  };

  /* ------------------------------------------------------------ writes */

  const update = useUpdateReference();
  const remove = useDeleteReference();

  const openDetail = useCallback(
    (id: string) => {
      setEditWhy(false);
      setParam("ref", id);
    },
    [setParam],
  );
  const openWithWhy = useCallback(
    (id: string) => {
      setEditWhy(true);
      setParam("ref", id);
    },
    [setParam],
  );
  const recreate = useCallback(
    (reference: Reference) =>
      navigate("/dashboard/workshop", { state: { draftPrompt: recreatePrompt(reference) } }),
    [navigate],
  );
  const collect = useCallback(
    (reference: Reference, anchor: HTMLElement) => setPicker({ references: [reference], anchor }),
    [],
  );
  const onSuggestion = useCallback(
    (reference: Reference, decision: "accept" | "dismiss") => {
      if (preview) return;
      update.mutate({ referenceId: reference.id, suggestion: decision });
    },
    [preview, update],
  );
  const toggleSelect = useCallback(
    (id: string) => setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id])),
    [],
  );

  const move = (references: Reference[], brandId: string | null) => {
    if (preview) return;
    const moving = references.filter((r) => r.brandId !== brandId);
    if (moving.length === 0) return;
    const before = new Map(moving.map((r) => [r.id, r.brandId]));
    for (const r of moving) update.mutate({ referenceId: r.id, brandId });
    const where = brandId ? (brands.find((b) => b.id === brandId)?.name ?? "that brand") : "Everyone";
    queue.add({
      title: `Moved to ${where}`,
      description:
        moving.length === 1
          ? `“${moving[0].title}” is in ${where}${brandId ? "'s" : "'s"} references now.`
          : `${moving.length} references are in ${where}'s references now.`,
      action: {
        label: "Undo",
        onPress: () => {
          for (const r of moving) update.mutate({ referenceId: r.id, brandId: before.get(r.id) ?? null });
        },
      },
    });
    if (selectedId && moving.some((r) => r.id === selectedId)) setParam("ref", null);
  };

  const deleteReferences = (references: Reference[]) => {
    if (preview) return;
    for (const r of references) remove.mutate(r.id);
    if (selectedId && references.some((r) => r.id === selectedId)) setParam("ref", null);
  };

  const selectedReferences = useMemo(
    () => visible.filter((r) => selectedIds.includes(r.id)),
    [visible, selectedIds],
  );
  const stopSelecting = () => setSelectedIds([]);
  useEffect(() => {
    if (selectedIds.length === 0) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !picker && stopSelecting();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedIds.length, picker]);
  /* The picker is positioned on the tile's Collect button. Once that tile
     leaves the wall the button is gone and the picker would sit in the corner. */
  useEffect(() => {
    if (picker && !picker.anchor.isConnected) setPicker(null);
  }, [picker, visible]);

  const deleteCollection = useDeleteCollection();

  /* -------------------------------------------------------------- bar */

  const scopeOptions: WallFacetOption<ReferenceScope>[] = isGuest
    ? [{ value: "brand", label: activeBrandName ?? "This brand" }]
    : [
        {
          value: "brand-everyone",
          label: `${activeBrandName ?? "This brand"} + Everyone`,
          disabled: !scopeAvailable,
          note: scopeAvailable ? undefined : "No brand is selected in this workspace",
        },
        { value: "brand", label: `${activeBrandName ?? "This brand"} only`, disabled: !scopeAvailable },
        { value: "global", label: "Everyone only" },
      ];

  const filterGroups: WallFilterGroup[] = [
    {
      key: "kind",
      caption: "Kind",
      mode: "multi",
      disabled: false,
      options: KIND_TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t], selected: types.includes(t) })),
      pick: (v) => pickType(v as ReferenceType),
    },
    {
      key: "source",
      caption: "Source",
      mode: "single",
      disabled: false,
      options: PLATFORM_OPTIONS.map((p) => ({ value: p, label: PLATFORM_LABEL[p], selected: platform === p })),
      pick: (v) => {
        setPlatform((cur) => (cur === v ? undefined : (v as ReferencePlatform)));
        setPage(1);
      },
    },
    ...(collectionId
      ? []
      : [
          {
            key: "scope",
            caption: "Scope",
            mode: "one" as const,
            disabled: !scopeAvailable,
            options: scopeOptions
              .filter((o) => !o.disabled)
              .map((o) => ({ value: o.value, label: o.label, selected: effectiveScope === o.value })),
            pick: (v: string) => {
              setScope(v as ReferenceScope);
              setPage(1);
            },
          },
        ]),
  ];
  const filterSummary = [
    ...types.map((t) => TYPE_LABEL[t]),
    ...(platform ? [PLATFORM_LABEL[platform]] : []),
    ...(effectiveScope !== defaultScope && !collectionId ? [scopeLabel(effectiveScope)] : []),
  ];

  const readout = (
    <WallReadout
      isLoading={isLoading}
      isError={isError}
      filtered={filtered}
      total={shownTotal}
      libraryTotal={collectionId ? null : libraryTotal}
      atLeast={view.atLeast}
      noun="reference"
    />
  );

  const searchField = (
    <WallSearch
      value={q}
      onChange={setQ}
      onSubmit={submitSearch}
      label="Search references"
      trailing={
        <>
          {searchNeedsMore ? (
            <span className="tt-label shrink-0 whitespace-nowrap uppercase text-co-faint">2 min</span>
          ) : null}
          {submittedQ ? (
            <button
              type="button"
              onClick={() => runSearch("")}
              aria-label={`Clear the search for ${submittedQ}`}
              className="grid size-4 shrink-0 place-items-center text-co-faint transition-colors hover:text-co-text"
            >
              <X className="size-3.5" strokeWidth={2.25} aria-hidden="true" />
            </button>
          ) : null}
        </>
      }
    />
  );

  const collectionFacet = (
    <MenuTrigger>
      <button type="button" className="wb-facet" aria-label={`Collection: ${openCollection?.name ?? "All"}`}>
        <span className="wb-caption wb-caption--facet" aria-hidden="true">
          Collection
        </span>
        <span className="wb-facet-value">{openCollection?.name ?? "All"}</span>
      </button>
      <Popover placement="bottom start" offset={16} className="ref-chrome-pop min-w-[220px]">
        <Menu
          className="flex max-h-[360px] flex-col overflow-y-auto p-1.5 outline-none"
          onAction={(key) => setParam("collection", key === "all" ? null : String(key))}
        >
          <MenuItem id="all" className="ref-menu-row">All references</MenuItem>
          {scopedCollections.map((c) => (
            <MenuItem key={c.id} id={c.id} className="ref-menu-row" textValue={c.name}>
              <span className="flex-1 truncate">{c.name}</span>
              <span className="ref-mono-faint">{c.count}</span>
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );

  /* ------------------------------------------------------------ header */

  const deleteCollectionButton = () =>
    openCollection && canManageCollection(openCollection) ? (
      <button
        type="button"
        className="ref-more co-glass-control !size-8 text-co-alert-ink"
        aria-label={`Delete ${openCollection.name}`}
        onClick={() => setDeletingCollection(openCollection)}
      >
        <Trash2 aria-hidden className="size-4" />
      </button>
    ) : null;

  const subhead = openCollection
    ? `${openCollection.count} ${openCollection.count === 1 ? "reference" : "references"}${
        openCollection.brandId
          ? ` for ${brands.find((b) => b.id === openCollection.brandId)?.name ?? "a brand"}`
          : " for Everyone"
      }, collected by ${openCollection.createdBy.role === "Guest" ? "the client" : "the team"}.`
    : SUBHEAD;

  /* The collections row waits until there is something to collect. */
  const showCollections =
    !isPhone && (scopedCollections.length > 0 || !!collectionId || (canEdit && (libraryTotal ?? 0) > 0));

  const header = (
    <WallViewHeader
      title={openCollection?.name ?? "References"}
      subhead={subhead}
      below={
        showCollections ? (
          <CollectionsRow
            collections={scopedCollections}
            allCount={libraryTotal}
            openId={collectionId}
            onOpen={(id) => {
              stopSelecting();
              setParam("collection", id);
            }}
            onNew={canEdit ? (anchor) => setPicker({ references: [], anchor, startNew: true }) : undefined}
          />
        ) : undefined
      }
    >
      {searchField}
      <WallPlayback />
      <SubmissionActivity state={activity} canEdit={canEdit} />
      {deleteCollectionButton()}
      {/* The sticky bar's own Save: a round accent plus (user call, 12 Oct). */}
      {canEdit ? (
        <button
          type="button"
          className="grid size-8 flex-none place-items-center rounded-full bg-co-accent-fill text-co-on-accent"
          aria-label="Save"
          title="Save a reference"
          onClick={() => setSaveOpen(true)}
        >
          <Plus aria-hidden className="size-4" />
        </button>
      ) : null}
    </WallViewHeader>
  );

  /* The phone (References / Phone / Wall): the title with Save, the search
     full width, the collections, then the FILTER row with the readout. */
  const phoneFilterRow = (
    <div className="flex h-10 items-center justify-between border-b border-co-line">
      <MenuTrigger>
        <button type="button" className="wb-facet" aria-label={`Filter, ${filterSummary.length ? filterSummary.join(", ") : "none set"}`}>
          <SlidersHorizontal aria-hidden className="size-[13px] text-co-text" />
          <span className="ref-mono text-co-text">Filter{filterSummary.length ? ` · ${filterSummary.length}` : ""}</span>
        </button>
        <Popover placement="bottom start" offset={8} className="wb-glass-pop trend-filter-pop co-dropdown-panel w-[calc(100vw-24px)] max-w-[374px]">
          <WallFilterPanel groups={filterGroups} canClear={filterSummary.length > 0} onClearAll={reset} />
        </Popover>
      </MenuTrigger>
      {readout}
    </div>
  );

  const tileActions = {
    onOpen: openDetail,
    onAddWhy: canEdit ? openWithWhy : undefined,
    onRecreate: recreate,
    onCollect: canEdit ? collect : undefined,
    onSuggestion,
    onToggleSelect: canEdit ? toggleSelect : undefined,
  };

  return (
    <PlaybackProvider stageOpen={!!selected}>
      <DashboardPage className="ref-page overflow-visible">
        <div ref={rootRef} className="flex w-full flex-col">
          {isPhone ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <h1 className="ref-phone-title">{openCollection?.name ?? "References"}</h1>
                <span className="flex items-center gap-2">
                  <SubmissionActivity state={activity} canEdit={canEdit} />
                  {deleteCollectionButton()}
                  {canEdit ? (
                    <button type="button" className="grid size-9 place-items-center rounded-full bg-co-accent-fill text-co-on-accent" aria-label="Save" onClick={() => setSaveOpen(true)}>
                      <Plus aria-hidden className="size-4" />
                    </button>
                  ) : null}
                </span>
              </div>
              <div className="ref-phone-search mt-3.5">{searchField}</div>
              {scopedCollections.length > 0 || collectionId ? (
                <div className="ref-collections--phone mt-2.5">
                  <CollectionsRow
                    collections={scopedCollections}
                    allCount={null}
                    openId={collectionId}
                    onOpen={(id) => setParam("collection", id)}
                    onNew={canEdit ? (anchor) => setPicker({ references: [], anchor, startNew: true }) : undefined}
                  />
                </div>
              ) : null}
              <div className="mt-2.5">{phoneFilterRow}</div>
            </>
          ) : (
            <>
              {header}
              <WallBar
                condensed={{
                  active: pastHeader || rowSearchFocused,
                  row: (
                    <WallScrolledRow
                      title={openCollection?.name ?? "References"}
                      summary={filterSummary}
                      leading={collectionFacet}
                      panel={<WallFilterPanel groups={filterGroups} canClear={filterSummary.length > 0} onClearAll={reset} />}
                      search={
                        <span
                          className="contents"
                          onFocus={() => setRowSearchFocused(true)}
                          onBlur={(event) => {
                            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                              setRowSearchFocused(false);
                            }
                          }}
                        >
                          {searchField}
                        </span>
                      }
                      trailing={
                        <span className="flex flex-none items-center gap-3">
                          {readout}
                          {deleteCollectionButton()}
                          {canEdit ? (
                            <button
                              type="button"
                              className="grid size-8 place-items-center rounded-full bg-co-accent-fill text-co-on-accent"
                              aria-label="Save"
                              onClick={() => setSaveOpen(true)}
                            >
                              <Plus aria-hidden className="size-4" />
                            </button>
                          ) : null}
                        </span>
                      }
                    />
                  ),
                }}
                axes={
                  <>
                    <WallCaption>Kind</WallCaption>
                    <WallRail label="Filter by kind" options={KIND_OPTIONS} picked={types} onToggle={pickType} />
                    <WallHairline />
                    <WallFacet
                      caption="Source"
                      label="Filter by source"
                      value={platform}
                      options={SOURCE_OPTIONS}
                      anyLabel="Any"
                      onChange={(next) => {
                        setPlatform(next);
                        setPage(1);
                      }}
                    />
                    {!collectionId ? (
                      <>
                        <WallHairline />
                        <WallFacet
                          caption="Scope"
                          label="Filter by who it is saved for"
                          value={effectiveScope}
                          options={scopeOptions}
                          isDisabled={isGuest}
                          onChange={(next) => {
                            setScope(next ?? null);
                            setPage(1);
                          }}
                        />
                      </>
                    ) : null}
                  </>
                }
                readout={readout}
              />
            </>
          )}

          <main className={cn("pb-24", isPhone ? "pt-3" : "pt-6")} aria-labelledby="ref-wall-heading">
            <h2 id="ref-wall-heading" className="sr-only">
              The wall
            </h2>

            <WallFilterAnnouncer
              message={
                searchNeedsMore
                  ? "Type at least two characters to search"
                  : isLoading
                    ? "Reading the reference library"
                    : isError
                      ? "The reference library did not answer, so nothing is shown"
                      : filtered
                        ? `${view.atLeast ? "At least " : ""}${shownTotal} ${shownTotal === 1 ? "reference matches" : "references match"}: ${activeSummary.join(" · ")}`
                        : `${shownTotal} ${shownTotal === 1 ? "reference" : "references"}`
              }
            />

            {view.failed > 0 && !isError && !isLoading ? (
              <WallIncompleteNotice failed={view.failed} requested={view.requested} onRetry={refetch} ease="ref-ease" />
            ) : null}

            {isLoading ? (
              <SkeletonWall />
            ) : isError ? (
              <ErrorState
                minHeight={440}
                title="The library did not answer"
                body="The reference library could not be read, so nothing is shown. An empty wall here would be a guess, and a guess is worse than a gap."
                onRetry={refetch}
              />
            ) : visible.length === 0 ? (
              filtered ? (
                <FilteredEmptyState
                  minHeight={440}
                  title="Nothing matches"
                  body="The library answered, and nothing in it fits every filter at once. Try widening them."
                  activeFilters={activeSummary}
                  onClear={reset}
                />
              ) : collectionId ? (
                <EmptyState
                  minHeight={440}
                  icon={<BookmarkPlus aria-hidden />}
                  title="Nothing collected yet"
                  body="Collect references into it from a tile's Collect, or from the detail."
                />
              ) : (
                <EmptyState
                  minHeight={440}
                  icon={<BookmarkPlus aria-hidden />}
                  title="Nothing saved yet"
                  body="Paste a link or an idea into Save a reference. It gets analyzed in the background and shows up here."
                  actions={
                    canEdit ? (
                      <KitButton iconLeft={Plus} onClick={() => setSaveOpen(true)}>
                        Save
                      </KitButton>
                    ) : undefined
                  }
                />
              )
            ) : (
              <>
                <div className="co-wall-field">
                  <div
                    className={cn("co-wall ref-wall transition-opacity ref-ease", isFetching && "opacity-60")}
                    data-selecting={selectedIds.length > 0 || undefined}
                  >
                    {visible.map((reference) => (
                      <ReferenceTile
                        key={reference.id}
                        reference={reference}
                        canWrite={canManage(reference)}
                        selected={selectedIds.includes(reference.id)}
                        {...tileActions}
                      />
                    ))}
                    {isFetchingMore
                      ? Array.from({ length: MORE_SKELETON_COUNT }, (_, i) => (
                          <SkeletonTile key={`more-${i}`} height={SKELETON_HEIGHTS[i]} />
                        ))
                      : null}
                  </div>
                </div>
                <div ref={sentinelRef} aria-hidden="true" />
              </>
            )}
          </main>
        </div>

        {selectedReferences.length > 0 ? (
          <ReferenceSelectBar
            count={selectedReferences.length}
            canEdit={canEdit}
            canDelete={selectedReferences.every((r) => canManage(r))}
            onCollect={(anchor) => setPicker({ references: selectedReferences, anchor })}
            onDelete={() => setConfirmBulkDelete(true)}
            onClear={stopSelecting}
          />
        ) : null}

        <ReferenceDetail
          reference={selected}
          brands={brands}
          collections={allCollections}
          canEdit={!preview && canManage(selected)}
          canDelete={!preview && canManage(selected)}
          allowEveryone={allowEveryone}
          editWhy={editWhy}
          nav={
            selectedIndex >= 0
              ? {
                  index: selectedIndex,
                  count: visible.length,
                  onPrevious: selectedIndex > 0 ? () => openDetail(visible[selectedIndex - 1].id) : null,
                  onNext: selectedIndex < visible.length - 1 ? () => openDetail(visible[selectedIndex + 1].id) : null,
                }
              : undefined
          }
          onClose={() => setParam("ref", null)}
          onOpenCollection={(id) => {
            setParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete("ref");
              next.set("collection", id);
              return next;
            });
          }}
          onCollect={collect}
          onUpdate={(input) => !preview && update.mutate(input)}
          onMove={(reference, brandId) => move([reference], brandId)}
          onDelete={(reference) => deleteReferences([reference])}
        />

        {saveOpen ? (
          <ReferenceSaveSheet
            isOpen={saveOpen}
            onClose={() => setSaveOpen(false)}
            brands={brands}
            defaultBrandId={effectiveScope !== "global" || isGuest ? activeBrandId : null}
            allowEveryone={allowEveryone}
          />
        ) : null}

        {picker ? (
          picker.references.length === 0 ? (
            <CollectionPicker
              references={[]}
              anchor={picker.anchor}
              collections={[]}
              brands={brands}
              activeBrandId={activeBrandId}
              allowEveryone={allowEveryone}
              canManage={canManageCollection}
              onClose={() => setPicker(null)}
              startNew
            />
          ) : (
            <CollectionPicker
              references={picker.references.map((r) => visible.find((v) => v.id === r.id) ?? r)}
              anchor={picker.anchor}
              collections={allCollections}
              brands={brands}
              activeBrandId={activeBrandId}
              allowEveryone={allowEveryone}
              canManage={canManageCollection}
              onClose={() => setPicker(null)}
              startNew={picker.startNew}
              wallCollectionId={collectionId}
            />
          )
        ) : null}

        <ConfirmDelete
          isOpen={confirmBulkDelete}
          title={`Delete ${selectedReferences.length} ${selectedReferences.length === 1 ? "reference" : "references"}?`}
          description="They leave the library for everyone, with their analyses and their whys, and any strategy slide that uses one loses it."
          onCancel={() => setConfirmBulkDelete(false)}
          onConfirm={() => {
            setConfirmBulkDelete(false);
            deleteReferences(selectedReferences);
            stopSelecting();
          }}
        />

        <ConfirmDelete
          isOpen={!!deletingCollection}
          title="Delete this collection?"
          description={`“${deletingCollection?.name ?? ""}” goes for everyone. Its references stay in the library.`}
          onCancel={() => setDeletingCollection(null)}
          onConfirm={() => {
            const target = deletingCollection;
            setDeletingCollection(null);
            if (!target) return;
            if (collectionId === target.id) setParam("collection", null);
            deleteCollection.mutate(target.id);
          }}
        />

      </DashboardPage>
    </PlaybackProvider>
  );
}


